






























































import { defineComponent, computed, useContext, onMounted, useRoute, nextTick } from '@nuxtjs/composition-api'
import { storeToRefs } from 'pinia'
import { META_TAGS, PRODUCT_STATUS } from '@/common/utils/product/constants'

import { useProductStore } from '~/store/product'
import { usePdpStore } from '~/store/pdp'
import { useCloudflareStore, useCMSContentTargetsStore } from '~/store'

import { BreadcrumbsData } from '~/types/breadcrumbs'
import { ApplicationChiclet, Figure, ProductStatus as ProductStatusType } from '~/types/product/productTypes'

import Skeleton from '@/components/global/skeletons/Skeleton.vue'
// Left side components
import ProductSchema from '~/components/feature/PDP/ProductSchema.vue'
import Breadcrumbs from '~/components/global/navigation/Breadcrumbs.vue'
import RecombinantFlag from '~/components/feature/PDP/RecombinantFlag.vue'
import TrialSizeFlag from '~/components/feature/PDP/TrialSizeFlag.vue'
import PreviewNotice from '~/components/feature/PDP/PreviewNotice.vue'
import Title from '~/components/feature/PDP/Title.vue'
import CitationsCount from '~/components/feature/PDP/CitationsCount.vue'
import Alternatives from '~/components/feature/PDP/Alternatives.vue'

import AntibodyReferenceLinkPdpWrapper from '@/components/feature/PDP/AntibodyReferenceLinkPdpWrapper.vue'
import ApplicationTabPdpWrapper from '@/components/feature/PDP/ApplicationTabPdpWrapper.vue'
import FigureBlockPdpWrapper from '@/components/feature/PDP/FigureBlockPdpWrapper.vue'
import SupportingDataPdpWrapper from '@/components/feature/PDP/SupportingDataPdpWrapper.vue'
import ProductInformationPdpWrapper from '@/components/feature/PDP/ProductInformationPdpWrapper.vue'
import ProductListPdpWrapper from '@/components/feature/PDP/ProductListPdpWrapper.vue'
import CitationsPdpWrapper from '@/components/feature/PDP/CitationsPdpWrapper.vue'
import LegalStatementPdpWrapper from '@/components/feature/PDP/LegalStatementPdpWrapper.vue'
import InPageNavigationPdpWrapper from '@/components/feature/PDP/InPageNavigationPdpWrapper.vue'
import JapanRegulatoryProductBanner from '@/components/feature/PDP/JapanRegulatoryProductBanner.vue'

// Right side components
import ProductStatus from '~/components/feature/PDP/ProductStatus.vue'
import ToPurchasePdpWrapper from '~/components/feature/PDP/ToPurchasePdpWrapper.vue'
import PanelBuilderPdpWrapper from '~/components/feature/PDP/PanelBuilderPdpWrapper.vue'
import TechLinksPdpWrapper from '~/components/feature/PDP/TechLinksPdpWrapper.vue'
import ProductInquiryPdpWrapper from '~/components/feature/PDP/ProductInquiryPdpWrapper.vue'

import unavailableProducts from '@/static/unavailableProducts.json'
import { useCountry } from '~/composables/country/useCountry'
import {
  getProductStatus,
  isMonoclonalOrPolyClonalProductType,
  isProductHasTrialSizeSku,
} from '~/common/utils/product/product'
import usePDPAnalytics from '@/composables/analytics/usePDPAnalytics'
import { stripHTMLTags } from '~/common/utils/characters'
import { performance } from '@/common/utils/performance'
import { DEVKEY } from '~/common/constants/cms'
import { useCanonicalUrl } from '@/composables/useBaseUrl'
import { useCMSMeta } from '~/composables/cms/useCMSMeta'
import { useAfkDetector } from '~/composables/useAfkDetector'
import { useEbsStore } from '~/store/ebs'
import { Metadata } from '~/types/cms/metadata'
import useSearchAnalytics from '~/composables/analytics/useSearchAnalytics'
import { QueryStyle } from '~/types/search/searchTypes'

export default defineComponent({
  components: {
    Skeleton,
    ProductSchema,
    Breadcrumbs,
    RecombinantFlag,
    TrialSizeFlag,
    PreviewNotice,
    Title,
    CitationsCount,
    Alternatives,
    AntibodyReferenceLinkPdpWrapper,
    ApplicationTabPdpWrapper,
    FigureBlockPdpWrapper,
    SupportingDataPdpWrapper,
    ProductInformationPdpWrapper,
    ProductListPdpWrapper,
    CitationsPdpWrapper,
    LegalStatementPdpWrapper,
    InPageNavigationPdpWrapper,
    ProductStatus,
    ToPurchasePdpWrapper,
    PanelBuilderPdpWrapper,
    TechLinksPdpWrapper,
    ProductInquiryPdpWrapper,
    JapanRegulatoryProductBanner,
  },
  setup() {
    const ctx = useContext()
    const productStore = useProductStore()
    const currentProduct = computed(() => productStore.currentProduct)
    const pdpStore = usePdpStore()
    const ebsStore = useEbsStore()
    const { showGOPAvailability } = storeToRefs(ebsStore)
    const { template, citationsCount, isLoading, pdpPages, hasGetclpAnySkuWithNonZeroPrice, canDisplayPriceBrick } =
      storeToRefs(pdpStore)
    const cloudflareStore = useCloudflareStore()
    const { isJapan, isCN } = useCountry()
    const { trackProductView } = usePDPAnalytics()
    const { trackSiteSearch } = useSearchAnalytics()
    const route = useRoute()
    const isPreviewServer = ctx.$config.cst.isPreview
    const pdpPage = pdpPages.value[currentProduct.value.data.key]

    const cmsMetadata = computed(() => {
      return pdpPage?.seo_metadata?.map(
        (item) =>
          ({
            type: item?.type,
            value: item?.value,
            entity: item?.entityConnection?.edges?.[0]?.node?.cloudflare_id,
          } as Metadata)
      )
    })

    const breadcrumbRoutes = computed<BreadcrumbsData>(() => [
      {
        label: 'Products',
        url: '/browse',
      },
      {
        label: currentProduct.value.data.productCategory ?? currentProduct.value.data.productType,
        url: currentProduct.value.data.productCategory
          ? `/browse?categories=${encodeURIComponent(currentProduct.value.data.productCategory)}`
          : `/browse?producttype=${encodeURIComponent(currentProduct.value.data.productType)}`,
      },
      {
        label: currentProduct.value.data.name,
        url: '',
      },
    ])

    const isRecombinant = computed(() => currentProduct.value.data.attributes.lots?.lot.recombinant === 'true')

    const isTrialSizeOffered = computed(() => {
      // returns true only if its satisfies both conditions 1. produyctType is MonoClonal or PolyClonal 2. product has active Trial size sku
      return (
        isMonoclonalOrPolyClonalProductType(currentProduct.value.data) &&
        isProductHasTrialSizeSku(currentProduct.value.data)
      )
    })

    const titleData = computed(() => ({
      key: currentProduct.value.data.key,
      name: currentProduct.value.data.name,
      isProductDiscontinued: currentProduct.value.data.attributes.statusDates?.status === PRODUCT_STATUS.DISCONTINUED,
    }))

    const isJpRegulatoryProduct = computed(
      () => isJapan.value && unavailableProducts.japanRegulatoryProducts.includes(currentProduct.value.data.key)
    )

    const currentProductStatus = computed<ProductStatusType>(() => {
      const status = getProductStatus(currentProduct.value)
      if (status === PRODUCT_STATUS.RELEASED) {
        if (isJapan.value || isCN.value) {
          if (isJpRegulatoryProduct.value || !hasGetclpAnySkuWithNonZeroPrice.value) {
            return PRODUCT_STATUS.PENDING // Show `This product is currently unavailable` message
          }
        }
        if (!productStore.hasCurrentProductAnyPriceToDisplay) {
          return PRODUCT_STATUS.PENDING // Show `This product is currently unavailable` message
        }
      }

      return status
    })

    const metaData = () => {
      const data = []
      const productData = currentProduct.value.data
      const productName = stripHTMLTags(productData.name)
      const productTypes = ['Monoclonal Antibody', 'Secondary Antibody', 'Polyclonal Antibody']

      let descriptionText = ''

      if (productTypes.includes(productData.productType)) {
        let targetNames = ''
        let cited = ''
        let validated = ''
        let available = ''
        let highly = ''
        if (productName) {
          highly =
            ' Highly specific and rigorously validated in-house, ' +
            productName.replace('mAb', 'Monoclonal Antibody') +
            ' (CST #' +
            productData.key +
            ') is ready to ship.'
        }
        if (productData.targets) {
          if (productData.targets.length > 1) {
            targetNames =
              ' for studying ' +
              productData.targets
                .map((target: any) => target['target-name'])
                .sort()
                .join('/') +
              '.'
          } else {
            targetNames = ' for studying ' + productData.targets[0]['target-name'] + '.'
          }
        }

        if (citationsCount.value > 0) {
          cited = ' Cited in ' + citationsCount.value + ' publication'
          if (citationsCount.value > 1) {
            cited += 's.'
          }
        }

        if (productData.attributes.lots?.lot.applications?.application) {
          validated =
            ' Validated for ' +
            Array.from(
              new Set(
                productData.attributes.lots.lot.applications.application.map((app: any) => app.codeForDisplay).sort()
              )
            ).join(', ') +
            '.'
        }

        if (Object.keys(productData.skuData).length > 1) {
          available = ' Available in ' + Object.keys(productData.skuData).length + ' sizes.'
        }

        descriptionText = productData.productType + targetNames + cited + validated + available + highly
      } else if (productData.description) {
        if (productData.description.split('.', 2).at(1)?.length) {
          descriptionText = productData.description.split('.', 2).join('.') + '.'
        } else {
          descriptionText = productData.description
        }
      } else if (productData.attributes.productBackground) {
        if (productData.attributes.productBackground.split('.', 2).at(1)?.length) {
          descriptionText = productData.attributes.productBackground.split('.', 2).join('.') + '.'
        } else {
          descriptionText = productData.attributes.productBackground
        }
      }

      if (descriptionText.length) {
        data.push(
          {
            hid: META_TAGS.DESCRIPTION,
            name: META_TAGS.DESCRIPTION,
            content: stripHTMLTags(descriptionText),
          },
          {
            hid: META_TAGS.OG_DESCRIPTION,
            name: META_TAGS.OG_DESCRIPTION,
            content: stripHTMLTags(descriptionText),
          },
          {
            hid: META_TAGS.TWITTER_DESCRIPTION,
            name: META_TAGS.TWITTER_DESCRIPTION,
            content: stripHTMLTags(descriptionText),
          }
        )
      }

      if (currentProduct.value.data.attributes.images[0]) {
        data.push(
          {
            hid: META_TAGS.OG_IMAGE_WIDTH,
            name: META_TAGS.OG_IMAGE_WIDTH,
            content: currentProduct.value.data.attributes.images[0].imagewidth,
          },
          {
            hid: META_TAGS.OG_IMAGE_HEIGHT,
            name: META_TAGS.OG_IMAGE_HEIGHT,
            content: currentProduct.value.data.attributes.images[0].imageheight,
          }
        )

        if (currentProduct.value.data.attributes.images[0]?.filename) {
          data.push(
            {
              hid: META_TAGS.OG_IMAGE,
              name: META_TAGS.OG_IMAGE,
              content: getImageUrl(currentProduct.value.data.attributes.images[0]),
            },
            {
              hid: META_TAGS.TWITTER_IMAGE,
              name: META_TAGS.TWITTER_IMAGE,
              content: getImageUrl(currentProduct.value.data.attributes.images[0]),
            }
          )
        }
      }

      if (!currentProduct.value.data.productCategory) {
        data.push({
          name: 'robots',
          content: 'noindex',
        })
      }

      data.push(
        {
          hid: META_TAGS.OG_TITLE,
          name: META_TAGS.OG_TITLE,
          content: productName,
        },
        {
          hid: META_TAGS.OG_URL,
          name: META_TAGS.OG_URL,
          content: ctx.$config.cst.domainDefault + ctx.route.value.fullPath,
        },
        {
          hid: META_TAGS.OG_TYPE,
          name: META_TAGS.OG_TYPE,
          content: 'product',
        },
        {
          hid: META_TAGS.TWITTER_TITTLE,
          name: META_TAGS.TWITTER_TITTLE,
          content: productName,
        },
        {
          hid: META_TAGS.TWITTER_URL,
          name: META_TAGS.TWITTER_URL,
          content: ctx.$config.cst.domainDefault + ctx.route.value.fullPath,
        },
        {
          hid: META_TAGS.TWITTER_CARD,
          name: META_TAGS.TWITTER_CARD,
          content: 'summary_large_image',
        }
      )

      return data
    }

    const metaLinks = () => {
      const links = []

      if (currentProduct.value.data.attributes.images[0]?.filename) {
        links.push({
          rel: 'image_src',
          href: getImageUrl(currentProduct.value.data.attributes.images[0]),
        })
      }

      links.push(
        {
          rel: 'canonical',
          href: useCanonicalUrl(currentProduct.value.data.attributes.sefUrl),
        },
        {
          rel: 'alternate',
          hreflang: 'en',
          href: ctx.$config.cst.domainDefault + route.value.fullPath,
        },
        {
          rel: 'alternate',
          hreflang: 'zh-CN',
          href: ctx.$config.cst.domainChina + route.value.fullPath,
        },
        { rel: 'alternate', hreflang: 'ja-JP', href: ctx.$config.cst.domainJapan + route.value.fullPath },
        { rel: 'alternate', hreflang: 'x-default', href: ctx.$config.cst.domainDefault + route.value.fullPath }
      )

      return links
    }

    const getImageUrl = (image: Figure) => {
      let url = `${ctx.$config.cst.mediaUrl}/product/image/${image.filename}`
      if (
        cloudflareStore.images[image['dam-media-id']] &&
        cloudflareStore.images[image['dam-media-id']].variants.PUBLIC
      ) {
        url = cloudflareStore.images[image['dam-media-id']].variants.PUBLIC
      }
      return url
    }

    useCMSMeta(cmsMetadata.value, {
      title: stripHTMLTags(currentProduct.value.data.name),
      meta: metaData(),
      link: metaLinks(),
    })

    onMounted(async () => {
      // TODO: remove trackSiteSearch on ATG sunset
      if (route.value.query.qs) {
        trackSiteSearch({
          index_type: 'products',
          search_origin: document.referrer ? new URL(document.referrer).pathname : 'referrer',
          query_typed: route.value.query.qt as string,
          query_executed: route.value.query.qt as string,
          results_quantity: undefined,
          results_page_quantity: undefined,
          filters: undefined,
          response_time: undefined,
          sort_by: undefined,
          query_style: route.value.query.qs as QueryStyle,
        })
      }

      trackProductView(currentProduct.value.data.key)
      if (route.value.hash) {
        await nextTick()
        document.querySelector(route.value.hash)?.scrollIntoView()
      }
    })

    useAfkDetector(() => {
      if (showGOPAvailability.value) {
        ebsStore.refetchShipDates()
      }
    })

    const application = route.value.query.application
    const applicationChiclets = productStore.getChicletsForApplicationTab(currentProduct.value.data.key)
    if (applicationChiclets.includes(application as ApplicationChiclet)) {
      pdpStore.setSelectedApplication({ group: application as string })
    }

    const debugInfo = {
      templateName: template.value?.title,
      templateId: `*******${template.value?.id.slice(-7)}`,
    }

    return {
      product: currentProduct,
      template,
      breadcrumbRoutes,
      isRecombinant,
      isTrialSizeOffered,
      titleData,
      citationsCount,
      currentProductStatus,
      isLoading,
      isJpRegulatoryProduct,
      isPreviewServer,
      debugInfo,
      canDisplayPriceBrick,
    }
  },
  async asyncData(ctx) {
    const productId = ctx.route.params.id

    if (!productId) {
      return ctx.redirect('/')
    }
    const productIdValidationRegex = /^\d{1,6}(?:\.\w+)?$/
    if (!productIdValidationRegex.test(productId)) {
      return ctx.error({ statusCode: 404, message: 'Product ID is not valid!' })
    }

    if (ctx.$config.cst.isProductSSRRedirectsEnabled && productId.includes('.')) {
      // Route param contains file extension (e.g. 4060.html)
      // This breaks the rest of a code therefore we need to redirect to a page without it
      return ctx.redirect('/products/' + productId.split('.')[0])
    }

    const startTime = performance.now()
    const performanceLogger = ctx.$logger.child({
      lgroup: 'perf',
      path: ctx.route.fullPath,
    })

    const productStore = useProductStore(ctx.$pinia)
    const pdpStore = usePdpStore(ctx.$pinia)
    const cloudflareStore = useCloudflareStore(ctx.$pinia)
    const contentTargetsStore = useCMSContentTargetsStore()
    pdpStore.$reset()
    pdpStore.setIsLoading(true)

    async function fetchProductInquiryAndLegalStatement() {
      if (ctx.payload?.productInquiryAndLegalStatement) {
        // SSG
        contentTargetsStore._update(ctx.payload.productInquiryAndLegalStatement)
      } else {
        await contentTargetsStore.fetchManyContentTargets([DEVKEY.PRODUCT_INQUIRY, DEVKEY.LEGAL_STATEMENT])
      }
    }

    try {
      await Promise.all([
        productStore.fetchFullProduct(productId),
        cloudflareStore.fetchCloudflareProductImages(productId),
        fetchProductInquiryAndLegalStatement(),
      ])

      const productType = productStore.products[productId].data.productType
      await pdpStore.fetchPDPTemplateAndPDPPage(productId, productType)
    } catch (err: any) {
      console.error(`Failed to load PDP async data for product: ${productId}`, err)
      if (process.static) {
        // Make sure that page gets generated even when invalid product id is provided
        return ctx.error({ statusCode: 404 })
      }
      return ctx.error({ statusCode: err.response?.status, message: err.message })
    }
    pdpStore.setIsLoading(false)

    performanceLogger.debug('[PDP asyncData] Total time: %dms', (performance.now() - startTime).toFixed(2))
  },
  head: {
    // Uncomment the following line to help with debugging of the intersection observer API (https://github.com/pomber/intersection-observer-debugger)
    // script: [{ src: 'https://unpkg.com/intersection-observer-debugger' }],
  },
})
